import { useEffect, useState } from "react";
import { Box, IconButton, Sheet, Typography } from "@mui/joy";
import { motion } from "framer-motion";
import { ArrowBendUpLeft, PaperPlaneRight, X } from "@phosphor-icons/react";
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

import MenuBar from '../AddPost/MenuBar';
import { apiFetch } from '../../../utils/apiClient';
import { convertHeicToJpg } from '../../../utils/convertHeicToJpg';
import { ALLOWED_TYPES, MAX_FILES, MAX_FILE_SIZE_MB } from '../../../config/configPost';

/** Столько же, сколько в приложении: комментарий — не запись. */
const MAX_LENGTH = 800;

/** Превью прикреплённого файла до отправки. */
function AttachmentPreview({ file, onRemove }) {
    const [url, setUrl] = useState(null);

    useEffect(() => {
        const objectUrl = URL.createObjectURL(file);
        setUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    const isVideo = file.type.startsWith('video/');

    return (
        <Box sx={{ position: 'relative' }}>
            {isVideo ? (
                <Box
                    component="video"
                    src={url}
                    muted
                    sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '8px', background: '#000', display: 'block' }}
                />
            ) : (
                <Box
                    component="img"
                    src={url}
                    alt={file.name}
                    sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '8px', display: 'block' }}
                />
            )}

            <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={onRemove}
                sx={(theme) => ({
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    minHeight: 24,
                    borderRadius: '50px',
                    p: 0.5,
                    borderColor: theme.vars.palette.neutral.softBg,
                    borderWidth: '3px',
                    borderStyle: 'solid',
                    backgroundColor: theme.vars.palette.neutral.softActiveBg,
                    '&:hover': { backgroundColor: theme.vars.palette.neutral.softHoverBg },
                })}
            >
                <X size={12} />
            </IconButton>
        </Box>
    );
}

/**
 * Форма комментария: форматирование, эмодзи и вложения — как в приложении.
 *
 * Текст уходит разметкой (HTML) — её же показывает лента комментариев, поэтому
 * жирное, списки и ссылки переживают отправку. Файлы сначала заливаются на
 * `/api/post/upload`, а в комментарий уходят уже путями: тот же порядок, что и
 * у создания записи.
 *
 * @param {{id: number, name: string}|null} replyTarget - на чей комментарий отвечаем
 * @param {Function} onCancelReply - отменить ответ (крестик на плашке)
 */
export function AddComment({ globalPostId = 0, linkHash = null, setNewComments = () => { }, replyTarget = null, onCancelReply = () => { } }) {
    const [attachments, setAttachments] = useState([]);
    const [charCount, setCharCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Сообщение о том, что комментарий ушёл на проверку: это не ошибка, но и
    // не «опубликовано», поэтому отдельно от error.
    const [notice, setNotice] = useState(null);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Placeholder.configure({ placeholder: 'Написать комментарий…' }),
            Link.configure({
                autolink: true,
                openOnClick: false,
                linkOnPaste: true,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
        ],
        content: '',
        onUpdate: ({ editor }) => setCharCount(editor.getText().trim().length),
    });

    const isOverLimit = charCount > MAX_LENGTH;
    const isActive = (charCount > 0 || attachments.length > 0) && !isOverLimit && !loading;

    const handleFileChange = async (e) => {
        setError(null);

        const files = Array.from(e.target.files);
        const availableSlots = MAX_FILES - attachments.length;

        if (availableSlots <= 0) {
            setError(`Можно прикрепить не более ${MAX_FILES} файлов`);
            e.target.value = null;
            return;
        }

        const newFiles = [];

        for (let file of files.slice(0, availableSlots)) {
            if (file.type === 'image/heic' || file.type === 'image/heif') {
                try {
                    file = await convertHeicToJpg(file, 0.8);
                } catch {
                    setError(`Не удалось обработать файл ${file.name}`);
                    e.target.value = null;
                    return;
                }
            }

            if (!ALLOWED_TYPES.includes(file.type)) {
                setError(`Файл «${file.name}» имеет неподдерживаемый формат`);
                e.target.value = null;
                return;
            }

            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`Файл «${file.name}» превышает ${MAX_FILE_SIZE_MB}MB`);
                e.target.value = null;
                return;
            }

            newFiles.push(file);
        }

        if (files.length > availableSlots) {
            setError(`Можно прикрепить не более ${MAX_FILES} файлов`);
        }

        setAttachments(prev => [...prev, ...newFiles]);
        e.target.value = null;
    };

    const uploadFiles = async () => {
        const formData = new FormData();
        attachments.forEach(file => formData.append('files', file));

        const res = await apiFetch('/api/post/upload', {
            method: 'POST',
            body: formData,
        });

        if (!res.ok) throw new Error('Ошибка при загрузке файлов');

        const data = await res.json();
        return data?.files ?? [];
    };

    const handleSubmit = async () => {
        if (!isActive) return;

        setLoading(true);
        setError(null);

        try {
            const uploaded = attachments.length > 0 ? await uploadFiles() : [];

            const res = await apiFetch(`/api/post/comments`, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: charCount > 0 ? editor.getHTML() : '',
                    id: globalPostId,
                    // Подтверждает переход по ссылке для записи «по ссылке» —
                    // без него сервер такую запись посторонним не отдаёт.
                    linkHash,
                    attachments: uploaded,
                    replyId: replyTarget?.id ?? null,
                }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                // Автопроверка объясняет отказ — показываем причину, иначе
                // человек не поймёт, что именно переписывать.
                const reasons = Array.isArray(data?.reasons) && data.reasons.length > 0
                    ? `: ${data.reasons.join('; ')}`
                    : '';
                throw new Error(`${data?.error ?? 'Ошибка при добавлении комментария'}${reasons}`);
            }

            const data = await res.json();

            // Комментарий на проверке всё равно показываем — но только автору
            // и с плашкой «На модерации»: иначе он молча исчезает после
            // отправки, и человек пишет его второй раз.
            setNotice(data?.pending
                ? 'Комментарий виден только вам, пока его не одобрят.'
                : null);
            setNewComments(data?.comment ?? {});

            editor?.commands.clearContent(true);
            setCharCount(0);
            setAttachments([]);
            onCancelReply();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Ctrl/⌘+Enter отправляет — привычная пара к Enter, который здесь делает
    // перенос строки (в редакторе с форматированием иначе нельзя).
    useEffect(() => {
        if (!editor) return;

        const onKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                handleSubmit();
            }
        };

        editor.view.dom.addEventListener('keydown', onKeyDown);
        return () => editor.view.dom.removeEventListener('keydown', onKeyDown);
    });

    // Нажали «Ответить» — курсор сразу в поле: иначе после прокрутки к форме
    // пришлось бы ещё и целиться в неё мышью.
    useEffect(() => {
        if (replyTarget && editor) editor.commands.focus('end');
    }, [replyTarget, editor]);

    return (
        <Box sx={{ mb: 2 }}>
            {replyTarget && (
                <Sheet
                    variant="soft"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        mb: 1,
                        borderRadius: '16px',
                    }}
                >
                    <ArrowBendUpLeft size={16} color="var(--joy-palette-primary-500)" />
                    <Typography level="body-sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        Ответ · {replyTarget.name}
                    </Typography>
                    <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={onCancelReply}
                        aria-label="Отменить ответ"
                        sx={{ borderRadius: '50px' }}
                    >
                        <X size={14} />
                    </IconButton>
                </Sheet>
            )}

            <MenuBar
                editor={editor}
                handleFileChange={handleFileChange}
                activeAttach={attachments.length < MAX_FILES}
                inputId="comment-file-input"
            />

            <Sheet
                variant="soft"
                onClick={() => editor?.commands.focus()}
                sx={{
                    borderRadius: '24px',
                    p: 2,
                    cursor: 'text',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        '& .ProseMirror': {
                            outline: 'none',
                            minHeight: 44,
                            '& p.is-editor-empty:first-of-type::before': {
                                content: '"Написать комментарий…"',
                                color: 'text.tertiary',
                                float: 'left',
                                height: 0,
                                pointerEvents: 'none',
                            },
                        },
                        '& p': { my: 0 },
                        '& h3': { my: 0 },
                        '& ul, & ol': { pl: 3, my: 0 },
                    }}
                >
                    <EditorContent editor={editor} />
                </Box>

                {attachments.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        {attachments.map((file, index) => (
                            <AttachmentPreview
                                key={`${file.name}-${index}`}
                                file={file}
                                onRemove={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', mt: 1 }}>
                    <Typography
                        level="body-xs"
                        sx={{ color: isOverLimit ? 'danger.500' : 'text.tertiary' }}
                    >
                        {charCount}/{MAX_LENGTH}
                    </Typography>

                    <motion.div
                        animate={{ opacity: isActive ? 1 : 0.4, scale: isActive ? 1 : 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            color="primary"
                            variant="solid"
                            loading={loading}
                            disabled={!isActive}
                            onClick={handleSubmit}
                            sx={{ width: 40, height: 40, borderRadius: "50%" }}
                        >
                            <PaperPlaneRight size={20} />
                        </IconButton>
                    </motion.div>
                </Box>
            </Sheet>

            {error && (
                <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}

            {notice && !error && (
                <Typography level="body-sm" color="warning" sx={{ mt: 1 }}>
                    {notice}
                </Typography>
            )}
        </Box>
    );
}
